import { ChangeEvent, useCallback, useRef } from "react";

import clsx from "clsx";

import { Icon } from "react-migration/components/Icon";

import { LayerTypeControlPageProps } from "../../types";
import { useControlTracking } from "../../ControlTrackingContext";
import { FilterToggle } from "../PlanningApplicationsLayerType/components/FilterToggle";
import { PermitClassification, PermitStatus } from "@landtechnologies/search-layer-contracts";
import { usePermitsContext } from "./Context/Context";
import { CheckboxTree, CheckboxTreeChange, TreeStructure } from "react-migration/components/CheckboxTree";
import { usePersistentToggleState } from "react-migration/components/CheckboxTree/usePersistentToggleState";
import { PermitTypeId } from "./state/usePermitFilterState";
import { NumericInput } from "react-migration/components/NumericInput";
import { RangeFilter } from "react-migration/domains/comparables/components/Filters/RangeFilter";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { UpgradeButton } from "react-migration/components/UpgradeButton";
import { RenderNodeBadge } from "react-migration/components/CheckboxTree/types";
import { DesignationHatch } from "react-migration/domains/constraints/components/Hatch";
export const planningStatuses: { id: PermitStatus; name: string }[] = [
  { id: "Approved", name: "Approved" },
  { id: "Withdrawn", name: "Withdrawn" },
  { id: "Pending", name: "Pending" },
  { id: "Rejected", name: "Rejected" },
  { id: "Uncategorized", name: "Uncategorized" },
];

const planningTypes: TreeStructure<PermitTypeId>["childNodes"] = [
  {
    id: "Conceptual",
    display_name: "Conceptual",
    childNodes: [],
  },
  {
    id: "Construction",
    display_name: "Construction",
    childNodes: [
      {
        id: "Construction.General",
        display_name: "General",
        childNodes: [],
      },
      {
        id: "Construction.Individual",
        display_name: "Individual",
        childNodes: [],
      },
      {
        id: "Construction.Notice",
        display_name: "Notice",
        childNodes: [],
      },
    ],
  },
  {
    id: "Modification",
    display_name: "Modification",
    childNodes: [
      {
        id: "Modification.Exempt",
        display_name: "Exempt",
        childNodes: [],
      },
      {
        id: "Modification.Modification",
        display_name: "Modification",
        childNodes: [],
      },
    ],
  },
  {
    id: "Uncategorized",
    display_name: "Unknown",
    childNodes: [],
  },
];

const typeFilters: TreeStructure = {
  id: "enabledTypes",
  display_name: "Permit Types",
  childNodes: planningTypes.map((type) => ({
    id: type.id,
    display_name: type.display_name,
    childNodes: type.childNodes || [],
  })),
};

const TYPE_SCHEMA = planningTypes.flatMap((node) => [{ key: node.id, showChildren: true }]);

const statusFilters = {
  id: "enabledStatuses",
  display_name: "Permit Statuses",
  childNodes: planningStatuses.map((status) => ({
    id: status.id,
    display_name: status.name,
    childNodes: [],
  })),
};

const STATUS_SCHEMA = statusFilters.childNodes.map((node) => ({ key: node.id }));

export function PermitsLayerControlPage({ disabled }: LayerTypeControlPageProps) {
  const { state, stateDispatch } = usePermitsContext();
  const { debouncedTrackControlChange } = useControlTracking();
  const descriptionInputRef = useRef<HTMLInputElement>(null);

  const hasStatusFilters = hasFeature(Feature.permitStatusFilters);
  const hasAllFilters = hasFeature(Feature.permitAllFilters);

  const persistenceKey = `us-expanded-permit-types`;
  const [getCollapsed, setCollapsed] = usePersistentToggleState(persistenceKey);

  const { enabledClassifications, enabledStatuses, enabledTypes, freeTextSearch, maxAge, projectSize } = state;

  const handleFreeTextSearchChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      debouncedTrackControlChange({ filterName: "description", filterState: ev.target.value });
      stateDispatch({ type: "SET_FREETEXT_FILTER", value: ev.target.value });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handleClearFreeTextSearch = useCallback(() => {
    debouncedTrackControlChange({ filterName: "freetextFilter", filterState: "" });
    stateDispatch({ type: "SET_FREETEXT_FILTER", value: "" });
    if (descriptionInputRef.current) {
      descriptionInputRef.current.value = "";
    }
  }, [stateDispatch, debouncedTrackControlChange]);

  const handleClassificationChanged = useCallback(
    (classification: PermitClassification) => {
      const newClassifications = state.enabledClassifications.includes(classification)
        ? state.enabledClassifications.filter((c) => c !== classification)
        : [...state.enabledClassifications, classification];

      debouncedTrackControlChange({
        filterName: "enabledClassifications",
        filterState: newClassifications,
      });
      stateDispatch({ type: "SET_ENABLED_CLASSIFICATIONS", value: newClassifications });
    },
    [stateDispatch, debouncedTrackControlChange, state.enabledClassifications]
  );

  const handleStatusChange = useCallback<CheckboxTreeChange>(
    ({ visibleNodeIds }) => {
      // remove trees root node id
      const filteredVisibleNodeIds = visibleNodeIds.filter((id) => id !== "enabledStatuses");

      debouncedTrackControlChange({ filterName: "status", filterState: filteredVisibleNodeIds });

      return stateDispatch({
        type: "SET_ENABLED_STATUSES",
        value: filteredVisibleNodeIds as PermitStatus[],
      });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handleTypeChange = useCallback<CheckboxTreeChange>(
    ({ visibleNodeIds }) => {
      // remove trees root node id
      const filteredVisibleNodeIds = visibleNodeIds.filter((id) => id !== "enabledTypes");

      debouncedTrackControlChange({ filterName: "type", filterState: filteredVisibleNodeIds });

      return stateDispatch({
        type: "SET_ENABLED_TYPES",
        value: filteredVisibleNodeIds as PermitTypeId[],
      });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handleSubmissionAgeChange = useCallback(
    (value: number) => {
      debouncedTrackControlChange({ filterName: "permitMaxAge", filterState: value });

      return stateDispatch({
        type: "SET_SUBMISSION_MAX_AGE",
        value: isNaN(value) ? undefined : value,
      });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handlePlotSizeChangeMin = useCallback(
    (value: number) => {
      debouncedTrackControlChange({ filterName: "projectSizeMin", filterState: value });

      return stateDispatch({
        type: "SET_PROJECT_SIZE_MIN",
        value: value,
      });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const handlePlotSizeChangeMax = useCallback(
    (value: number) => {
      debouncedTrackControlChange({ filterName: "projectSizeMax", filterState: value });

      return stateDispatch({
        type: "SET_PROJECT_SIZE_MAX",
        value: value,
      });
    },
    [stateDispatch, debouncedTrackControlChange]
  );

  const renderNodeBadge = useCallback<RenderNodeBadge>(
    (node) => {
      // We're dealing with underlying enum values on the document here,
      // not the sub categories, so we have to do a little translating
      const enumToSubCategoryIdMap = {
        Approved: "environmental_permits_approved",
        Withdrawn: "environmental_permits_withdrawn",
        Pending: "environmental_permits_pending",
        Rejected: "environmental_permits_rejected",
        Uncategorized: "environmental_permits_unknown",
      };

      return (
        <DesignationHatch
          id={enumToSubCategoryIdMap[node.id as keyof typeof enumToSubCategoryIdMap]}
          disabled={disabled}
        />
      );
    },
    [disabled]
  );

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {!hasAllFilters && (
          <UpgradeButton className="w-full" source="Permits">
            Unlock more filters
          </UpgradeButton>
        )}
        <div className="pt-2 pb-4 space-y-2 border-b border-neutral-200">
          <label htmlFor="permit-freetext-search" className={clsx("font-semibold", { "opacity-50": disabled })}>
            Search
          </label>
          <div className="relative">
            <input
              ref={descriptionInputRef}
              id="permit-freetext-search"
              type="text"
              defaultValue={freeTextSearch}
              onChange={handleFreeTextSearchChange}
              disabled={disabled || !hasAllFilters}
              placeholder='Search (use "quotes" for exact matches)'
              className={clsx(
                "rounded-lg border px-3 grow w-full",
                "bg-background-input border-border-action placeholder:text-text-base-tertiary",
                "h-9 placeholder:text-sm text-sm",
                "focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                {
                  "opacity-50 cursor-not-allowed": disabled,
                  "pr-8": freeTextSearch,
                }
              )}
            />
            <button
              type="button"
              onClick={handleClearFreeTextSearch}
              disabled={disabled || !hasAllFilters}
              className={clsx(
                "absolute right-2 top-1/2 transform -translate-y-1/2",
                "w-4 h-4 flex items-center justify-center",
                "text-neutral-400 hover:text-neutral-600",
                "transition-colors duration-200",
                { "opacity-50 cursor-not-allowed": disabled }
              )}
              aria-label="Clear search filter"
            >
              <Icon
                icon="icon-lt-close-line"
                size="sm"
                extraClasses="w-3 h-3 flex items-center justify-center"
                cursor="pointer"
              />
            </button>
          </div>
        </div>

        <div className="pt-2 pb-4 space-y-2 border-b border-neutral-200">
          <div className="space-y-2">
            <>
              <FilterToggle
                disabled={disabled || !hasAllFilters}
                name="Residential"
                prefix="permits"
                color="#ff3665"
                enabled={enabledClassifications.includes("Residential")}
                setEnabled={() => handleClassificationChanged("Residential")}
                hidePrefix
              />
              <FilterToggle
                disabled={disabled || !hasAllFilters}
                name="Commercial"
                prefix="permits"
                color="#8e36ff"
                enabled={enabledClassifications.includes("Commercial")}
                setEnabled={() => handleClassificationChanged("Commercial")}
                hidePrefix
              />
              <FilterToggle
                disabled={disabled || !hasAllFilters}
                name="Other"
                prefix="permits"
                color="#30b3df"
                enabled={enabledClassifications.includes("Other")}
                setEnabled={() => handleClassificationChanged("Other")}
                hidePrefix
              />
            </>
          </div>
        </div>
      </div>

      <div>
        <CheckboxTree
          title={<h1 className="text-sm">Statuses</h1>}
          treeStructure={statusFilters}
          treeSchema={STATUS_SCHEMA}
          visibleNodeIds={enabledStatuses}
          onChange={handleStatusChange}
          disabled={disabled || !hasStatusFilters}
          renderNodeBadge={renderNodeBadge}
        />
      </div>

      <div>
        <CheckboxTree
          title={<h1 className="text-sm">Types</h1>}
          treeStructure={typeFilters}
          treeSchema={TYPE_SCHEMA}
          visibleNodeIds={enabledTypes}
          onChange={handleTypeChange}
          disabled={disabled || !hasAllFilters}
          getCollapsed={getCollapsed}
          setCollapsed={setCollapsed}
        />
      </div>

      <div>
        <div className="flex items-center justify-between h-10">
          <h1 className="text-sm">Submissions within the last</h1>
        </div>
        <div className="flex items-center gap-2">
          <NumericInput
            isDisabled={disabled || !hasAllFilters}
            className="max-w-[80px]"
            size="small"
            value={maxAge}
            minValue={0}
            maxValue={35}
            onChange={handleSubmissionAgeChange}
            formatOptions={{
              maximumFractionDigits: 0,
            }}
          />

          <div className={clsx({ "opacity-50": disabled })}>years</div>
        </div>
      </div>

      <div>
        <RangeFilter
          disabled={disabled || !hasAllFilters}
          label={"Size"}
          min={projectSize[0] ?? undefined}
          units="acres"
          setMin={handlePlotSizeChangeMin}
          max={projectSize[1] ?? undefined}
          setMax={handlePlotSizeChangeMax}
          maxDecimals={2}
        />
      </div>
    </div>
  );
}
